import { useState, useRef, useEffect } from "react";
import { Button, SimpleGrid, Image, Text } from "@mantine/core";
import { IconUpload } from "@tabler/icons-react";
import { MAX_FILE_SIZE } from "utiles/url";

export default function ImageUploader({
  multiple = false,
  onChange,
  previewsPerRow = 4,
  buttonLabel = "Upload Image",
  defaultImage = null,
  accept,
}) {
  const fileInputRef = useRef(null);
  const [previews, setPreviews] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (defaultImage) {
      setPreviews([{ url: defaultImage, file: null }]);
    }
  }, [defaultImage]);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    setError(null);

    // parse accepted types into rules array
    const acceptString = accept
      ? accept
      : "image/jpeg, image/png, image/jpg, .pdf";
    const rules = acceptString
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);

    const isAllowedType = (file) => {
      // check against each rule
      return rules.some((rule) => {
        if (rule.startsWith(".")) {
          // extension rule
          return file.name.toLowerCase().endsWith(rule.toLowerCase());
        } else if (rule.endsWith("/*")) {
          const prefix = rule.replace("/*", "");
          return file.type.startsWith(prefix);
        } else {
          // exact mime type
          return file.type === rule;
        }
      });
    };

    const validFiles = files.filter((file) => {
      if (!isAllowedType(file)) {
        setError(
          `File "${file.name}" is not an accepted format. Allowed: ${acceptString}`,
        );
        return false;
      }
      if (file.size > MAX_FILE_SIZE) {
        setError(`File "${file.name}" is too large. Max size is 1MB.`);
        return false;
      }
      return true;
    });

    if (validFiles.length === 0) {
      e.target.value = "";
      return;
    }

    const previewUrls = validFiles.map((file) => ({
      url: URL.createObjectURL(file),
      file,
    }));

    setPreviews(previewUrls);
    onChange && onChange(validFiles);
  };

  return (
    <>
      {previews.length === 0 && (
        <Button
          onClick={() => fileInputRef.current.click()}
          variant="outline"
          leftIcon={<IconUpload size={16} />}
          fullWidth
          style={{ width: "30%" }}
        >
          {buttonLabel}
        </Button>
      )}

      <input
        type="file"
        ref={fileInputRef}
        accept={accept ? accept : "image/jpeg, image/png, image/jpg, .pdf"}
        style={{ display: "none" }}
        onChange={handleFileChange}
        multiple={multiple}
      />

      {error && (
        <Text color="red" size="xs" mt={5}>
          {error}
        </Text>
      )}

      {previews.length > 0 && (
        <SimpleGrid
          cols={previewsPerRow}
          spacing="md"
          style={{ cursor: "pointer" }}
          onClick={() => fileInputRef.current.click()}
        >
          {previews.map((img, idx) => (
            <Image
              key={idx}
              src={img.url}
              radius="md"
              height={100}
              fit="contain"
              alt="Preview"
            />
          ))}
        </SimpleGrid>
      )}
    </>
  );
}
